// Implements the TradingView JS API defined at:
// https://github.com/tradingview/charting_library/wiki/JS-Api
import { WebSocketRequestUnsubscribeSubscription } from '@katanaperps/katana-perps-sdk';

import {
  fetchTradesOhlcvBars,
  MAX_CANDLES_INTERNAL_LIMIT,
} from 'api/apiExchange';
import {
  startPublicSubscription,
  stopSubscription,
  listenToWebsocket,
  stopListenFromWebsocket,
} from 'api/services/webSocketApiService';
import { EXCHANGE_DECIMALS, KATANA_PERPS } from 'config/constants';
import { createMarketSymbol } from 'lib/exchange';
import { getStateOutsideOfComponent } from 'reduxState/store';

import {
  SupportedResolutionsType,
  supportedResolutionsString,
  convertTradingviewIntervalToSdk,
} from './chartInterval';
import { prepareApiBarsForChart } from './datafeedUtils';
import {
  SubscribeBarsCallback,
  OnReadyCallback,
  QuotesErrorCallback,
  ResolveCallback,
  HistoryCallback,
  IBasicDataFeed,
  SearchSymbolsCallback,
  ResolutionString,
  PeriodParams,
  DatafeedErrorCallback,
  SymbolResolveExtension,
} from './external/charting_library';
import { ExtendedSymbolInfo, getSymbolInfo } from './tokenResolve';

function onReady(callback: OnReadyCallback): void {
  setTimeout(() =>
    callback({
      // symbols_types: [{ name: 'token', value: 'token' }],
      supported_resolutions: supportedResolutionsString,
      supports_marks: false,
      supports_timescale_marks: false,
      supports_time: false,
    })
  );
}
function resolveSymbol(
  symbolName: string, // IDEX:IDEX/ETH
  onSymbolResolvedCallback: ResolveCallback,
  _onResolveErrorCallback: DatafeedErrorCallback,
  _extension?: SymbolResolveExtension
): void {
  const [base, quote] = symbolName.split('/');

  if (!base || !quote) {
    console.warn('Symbol not found', symbolName);
    return;
  }

  getStateOutsideOfComponent().then(state => {
    const visibleDecimals =
      state.markets.listedMarkets.tickSizes?.[`${base}-${quote}`]
        ?.visibleDecimals ?? EXCHANGE_DECIMALS;
    const symbolInfo = getSymbolInfo(base, quote, visibleDecimals);
    onSymbolResolvedCallback(symbolInfo);
  });
}

/** If our API return "noData", we don't want fetch before that time in history */
const cachedNoDataMarketsFromTs: {
  [market: string]: number;
} = {};

async function getBars(
  symbolInfo: ExtendedSymbolInfo,
  resolution: ResolutionString,
  periodParams: PeriodParams,
  onHistoryCallback: HistoryCallback,
  onErrorCallback: QuotesErrorCallback
) {
  try {
    const { baseSymbol, quoteSymbol } = symbolInfo;
    const { from, to, countBack } = periodParams;

    const marketSymbol = createMarketSymbol(baseSymbol, quoteSymbol);
    const noDataFromTs = cachedNoDataMarketsFromTs[marketSymbol];

    const fromMs = from * 1000;
    const toMs = to * 1000;

    console.log(
      `[ChartFeed] ${marketSymbol} ${from}-${to} Back:${countBack} Resolution:${resolution} NDF:${
        noDataFromTs ?? 'No'
      }`
    );
    // console.table({
    //   marketSymbol,
    //   from: new Date(fromMs).toISOString(),
    //   to: new Date(toMs).toISOString(),
    //   countBack,
    //   resolution,
    //   noDataFromTs: noDataFromTs ? new Date(noDataFromTs).toISOString() : false,
    // });

    if (toMs <= noDataFromTs) {
      console.log(
        `[ChartFeed] ${marketSymbol} cached: API previously indicated noData`
      );
      onHistoryCallback([], { noData: true });
      return;
    }

    const candlesResponse = await fetchTradesOhlcvBars({
      marketSymbol,
      resolution: convertTradingviewIntervalToSdk(
        resolution as SupportedResolutionsType
      ),
      countBack,
      start: fromMs,
      end: toMs,
      limit: MAX_CANDLES_INTERNAL_LIMIT,
    });

    if (candlesResponse.items?.length >= MAX_CANDLES_INTERNAL_LIMIT - 1) {
      console.error(
        `Chart LIMIT reached on ${baseSymbol}-${quoteSymbol} using ${resolution} interval on range ${from}-${to}`
      );
    }
    const bars = candlesResponse.items;

    // On noData we already know, that there will be no data on next fetch
    if (candlesResponse.noData && bars.length > 0) {
      cachedNoDataMarketsFromTs[marketSymbol] = bars[0].start;
    }
    if (candlesResponse.noData && bars.length === 0) {
      onHistoryCallback([], { noData: true });
      return;
    }
    return onHistoryCallback(prepareApiBarsForChart(bars));
  } catch (error) {
    console.warn('Chart error fetching bars', error);
    return onErrorCallback('Request failed');
  }
}

const subscriptions: {
  [subscriberUID: string]: {
    id: number;
    subscription: WebSocketRequestUnsubscribeSubscription;
  };
} = {};

// symbolInfo: LibrarySymbolInfo, resolution: ResolutionString, onTick: SubscribeBarsCallback, listenerGuid: string, onResetCacheNeededCallback: () => void
async function subscribeBars(
  symbolInfo: ReturnType<typeof getSymbolInfo>,
  resolution: string,
  onTick: SubscribeBarsCallback,
  listenerGuid: string,
  _onResetCacheNeededCallback: () => void
) {
  const marketSymbol = createMarketSymbol(
    symbolInfo.baseSymbol,
    symbolInfo.quoteSymbol
  );
  const subscription = {
    name: 'candles',
    interval: convertTradingviewIntervalToSdk(
      resolution as SupportedResolutionsType
    ),
    markets: [marketSymbol] as string[],
  } as const;

  console.log('[chart:datafeed Subscribing to', JSON.stringify(subscription));

  startPublicSubscription(subscription);

  const id = listenToWebsocket('candles', event => {
    // TODO: we should no longer process once started diff market ?
    const { data } = event;
    // While re-subscribing websocket we can get some not accepted events
    if (data.interval !== subscription.interval) {
      console.warn('[chart:datafeed] Interval diff - ignoring', data.interval);
      return;
    }
    if (data.market !== marketSymbol) {
      console.warn('[chart:datafeed] Market diff - ignoring', data.market);
      return;
    }
    console.debug('[chart:datafeed] Candle Update', data.interval);
    const [formattedBar] = prepareApiBarsForChart([data]);
    setTimeout(() => {
      onTick(formattedBar);
    });
  });
  subscriptions[listenerGuid] = { id, subscription };
}

function unsubscribeBars(subscriberUID: string): void {
  const subscription = subscriptions[subscriberUID];
  if (subscription) {
    stopListenFromWebsocket(subscription.id);
    stopSubscription(subscription.subscription);
  }
}

async function searchSymbols(
  userInput: string,
  exchange: string,
  symbolType: string,
  onResult: SearchSymbolsCallback
): Promise<void> {
  const userInputUpper = userInput.toUpperCase().replace('/', '-');
  getStateOutsideOfComponent().then(state => {
    const markets = state.markets;

    const foundMarkets = userInput
      ? markets.listedMarkets.listedMarketsList.filter(market =>
          market.includes(userInputUpper)
        )
      : markets.listedMarkets.listedMarketsList;
    onResult(
      foundMarkets.map(market => {
        const [marketBase, marketQuote] = market.split('-');
        return {
          symbol: marketBase,
          // TODO: removed in 28.0.0? https://www.tradingview.com/charting-library-docs/latest/releases/release-notes#version-2800 ?
          full_name: marketBase,
          description: `${marketBase}-${marketQuote}`,
          exchange: KATANA_PERPS,
          ticker: `${marketBase}/${marketQuote}`,
          type: 'token',
        };
      })
    );
  });
}

// Shapes, we can draw buy sell points of user
// https://github.com/tradingview/charting_library/issues/3266

export const dataFeed: IBasicDataFeed = {
  onReady,
  resolveSymbol,
  getBars,
  searchSymbols,
  subscribeBars,
  unsubscribeBars,
};
